import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { useNavigate } from "react-router-dom";
import { restrictSpecialCharacters, ROLE_ROUTES } from "@utils/constants";
import { fetchInProgressFolders } from "@store/thunks/translationThunk";
import { setSelectedAssignedFolderId } from "@/store/slices/translatorAdminSlice";

const FolderDetailTableCommon = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { role } = useSelector((store) => store.auth);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    assignedFolders,
    assignedFiles,
    selectedAssignedProjectId,
    selectedAssignedFolderId,
  } = useSelector((store) => store.translatorAdmin);

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true); // 👈 track if more pages exist
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  const handleInputChange = (e) => {
   const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setSearchQuery(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setInitialLoading(true);
        if (selectedAssignedProjectId) { // Only fetch if projectId is valid
          const res = await dispatch(fetchInProgressFolders(selectedAssignedProjectId, searchQuery, pageIndex, pageSize));
          if (Array.isArray(res.payload)) { // Adjust based on thunk return structure
            if (res.payload.length < pageSize) {
              setHasNextPage(false);
            } else {
              setHasNextPage(true);
            }
          }
        } else {
          setError("No project selected");
        }
        setInitialLoading(false);
      } catch (error) {
        console.error("Error fetching folders:", error);
        setError("Failed to fetch folders: " + error.message);
        setInitialLoading(false);
      }
    };

    fetchProjects();
  }, [role, dispatch, selectedAssignedProjectId, searchQuery, pageIndex, pageSize]);

  const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;

  console.log("selectedAssignedProjectId:", selectedAssignedProjectId);
  console.log("assignedFolders:", assignedFolders);
  const assignedFolderArray = selectedAssignedProjectId ? assignedFolders[selectedAssignedProjectId] || [] : [];

  const handleFolderClick = ({ row }) => {
    if (selectedAssignedFolderId === row.folderId) {
      dispatch(setSelectedAssignedFolderId(row.folderId));
    } else {
      dispatch(setSelectedAssignedFolderId(row.folderId));
      if (!assignedFiles[row.folderId]) {
        dispatch(setSelectedAssignedFolderId(row.folderId));
      }
    }
    navigate(`${destinationRoute}/folders/files`);
  };

  const folderColumns = [
    {
      header: "Sr. No.",
      cell: ({ row, table }) => {
        const rowIndex = row.index;
        return pageIndex * pageSize + rowIndex + 1; // Serial number starts from 1
      },
      cellClassName: "text-center",
    },
    {
      header: "Folder Name",
      accessorKey: "folderName",
      cellClassName: "cursor-pointer hover:text-blue-600",
      onClick: handleFolderClick,
      cell: ({ row }) => (
        <div className="cursor-pointer text-[#ec7026] hover:text-[#d85d1a] focus:underline">
          {row.original.folderName}
        </div>
      ),
    },
    {
      header: "File Count",
      accessorKey: "fileCount",
      cell: ({ row }) => {
        console.log(row);
        const fileCount = row.original?.fileCount;
        return (
          <div className="text-center">
            {fileCount || 0}
          </div>
        );
      },
      cellClassName: "text-center",
    },
    {
      id: "createdAt",
      header: () => "Created At",
      accessorKey: "createdAt",
      cell: ({ row }) => {
        const deadline = row.original?.createdAt || row.createdAt;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
      cellClassName: "text-center",
    },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Folders..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="w-full p-4 sm:p-6 lg:p-8">
        {initialLoading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <p>{error}</p>
              <p className="mt-2">Please try again later or contact support.</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="w-full overflow-x-auto">
              {(!assignedFolderArray || assignedFolderArray.length === 0) ? (
                <div className="p-6">
                  <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
                    <p>No projects assigned yet.</p>
                    <p className="mt-2">
                      You have not been assigned any folders for validation. Please contact the
                      administrator if you believe this is an error.
                    </p>
                  </div>
                </div>
              ) : (
                <GenericTable
                  data={[...assignedFolderArray].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                  columns={folderColumns}
                  showPagination={true}
                  manualPagination={true}
                  pageIndex={pageIndex} 
                  pageSize={pageSize}  
                  hasNextPage={hasNextPage} 
                  onPaginationChange={({ pageIndex, pageSize }) => {
                    setPageIndex(pageIndex);
                    setPageSize(pageSize);
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderDetailTableCommon;